import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Table from "@/components/ui/table";
import Chip from "@/components/ui/chip";
import { Eye as EyeIcon } from "@/components/icons";
import type { TableColumn } from "@/types/component";
import {
  SUPPORT_TICKET_STATUS_CHIP_TYPE,
  SUPPORT_TICKET_STATUS_LABELS_TR,
  type SupportTicketListItem,
} from "../../types/support-tickets.types";
import { supportTicketsApi } from "../../services/support-tickets.api";
import SupportTicketDetailDrawer from "../SupportTicketDetailDrawer";
import styles from "./EducationSupportTicketsDrawer.module.scss";

export interface EducationSupportTicketsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  educationId: string;
  /** Modal başlığı altında küçük metin (örn. eğitim adı) */
  subtitle?: string;
}

function formatTicketDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

const EducationSupportTicketsDrawer: React.FC<
  EducationSupportTicketsDrawerProps
> = ({ isOpen, onClose, educationId, subtitle }) => {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState<SupportTicketListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchTickets = useCallback(async () => {
    if (!educationId?.trim()) return;
    setIsLoading(true);
    try {
      const res = await supportTicketsApi.getAll({
        page: currentPage,
        limit: pageSize,
        educationId,
      });
      setTickets(res.data);
      setTotalRecords(res.total);
    } catch (error) {
      console.error("Error fetching education support tickets:", error);
    } finally {
      setIsLoading(false);
    }
  }, [educationId, currentPage, pageSize]);

  useEffect(() => {
    if (isOpen) void fetchTickets();
  }, [isOpen, fetchTickets]);

  const handleViewDetail = (row: SupportTicketListItem) => {
    setSelectedTicketId(row.id);
    setDetailOpen(true);
  };

  const columns: TableColumn<SupportTicketListItem>[] = [
    {
      header: t("supportTickets.columns.ticketNo"),
      accessor: "requestNo",
    },
    {
      header: t("supportTickets.columns.personnelName"),
      accessor: "personnelName",
    },
    {
      header: t("supportTickets.columns.createdAt"),
      accessor: (row) => formatTicketDate(row.createdAt),
    },
    {
      header: t("supportTickets.columns.status"),
      accessor: (row) => (
        <Chip
          type={SUPPORT_TICKET_STATUS_CHIP_TYPE[row.status]}
          size="small"
          variant="filled"
        >
          {SUPPORT_TICKET_STATUS_LABELS_TR[row.status]}
        </Chip>
      ),
    },
  ];

  const renderActions = (row: SupportTicketListItem) => (
    <button
      type="button"
      className={styles.viewButton}
      onClick={(e) => {
        e.stopPropagation();
        handleViewDetail(row);
      }}
      aria-label={t("common.view")}
    >
      <EyeIcon width={20} height={20} />
    </button>
  );

  const footer = (
    <Button variant="contained" color="primary" type="button" onClick={onClose}>
      {t("buttons.close")}
    </Button>
  );

  return (
    <>
      <Modal
        title={t("educationManagement.supportTicketsModal.title")}
        isOpen={isOpen && !detailOpen}
        onClose={onClose}
        size="xl"
        customFooter={footer}
      >
        <div className={styles.body}>
          {subtitle ? <span className={styles.subtitle}>{subtitle}</span> : null}

          <Table
            columns={columns}
            data={tickets}
            isLoading={isLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            renderActions={renderActions}
            emptyMessage={t("educationManagement.supportTicketsModal.empty")}
            contentBasedHeight
          />
        </div>
      </Modal>

      <SupportTicketDetailDrawer
        ticketId={selectedTicketId}
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedTicketId(null);
        }}
        onTicketUpdated={fetchTickets}
      />
    </>
  );
};

export default EducationSupportTicketsDrawer;
